import { DeviceKind, DeviceOutcome, isFailureOutcome } from '../core';

/**
 * Emits a measurable, joinable failure→recovery signal for device acquisition.
 *
 * The problem this solves: today device telemetry is scattered across ~14 files,
 * each re-detecting its own transitions, and the most important state — a user
 * stuck unable to acquire a device — is invisible. This tracker centralises the
 * lifecycle into one place and emits a correlated triple per failure episode:
 *
 *   devices.<kind>.unavailable          { episodeId, reason }            — entered failure
 *   devices.<kind>.unavailable_changed  { episodeId, from, reason }      — failure reason changed
 *   devices.<kind>.recovered            { episodeId, from, durationMs }  — reached granted
 *   devices.<kind>.unrecovered          { episodeId, reason, durationMs} — left while still failed
 *
 * `episodeId` ties the failure to its eventual recovery (or non-recovery) so the
 * pair is directly joinable in DataDog. `durationMs` measures time-to-recover.
 * Global context (natteringRoundId, @usr.id) and RUM's @browser/@os are attached
 * automatically by the monitoring layer, so recovery rate can be segmented by
 * reason, browser, and round without any extra attributes here.
 */
export type TelemetrySink = (action: `devices.${string}`, extra?: Record<string, unknown>) => void;

export type RecoveryTrackerDeps = {
  /** Emits the event — in production this is `monitoring.trackAction`. */
  track: TelemetrySink;
  /** Wall clock, injectable for tests. */
  now: () => number;
  /** Generates a unique id per failure episode, injectable for tests. */
  newEpisodeId: () => string;
};

type Episode = {
  id: string;
  reason: DeviceOutcome;
  startedAt: number;
  /** How many failed outcomes were observed within this episode (retries). */
  attempts: number;
};

export type DeviceRecoveryTracker = {
  /** Feed every resolved outcome for a kind; the tracker derives the lifecycle. */
  recordOutcome: (kind: DeviceKind, outcome: DeviceOutcome) => void;
  /** Emit `unrecovered` for any open episodes — call when the flow tears down. */
  flushUnrecovered: () => void;
};

export const createDeviceRecoveryTracker = ({
  track,
  now,
  newEpisodeId,
}: RecoveryTrackerDeps): DeviceRecoveryTracker => {
  const episodes: Partial<Record<DeviceKind, Episode>> = {};

  const recordOutcome = (kind: DeviceKind, outcome: DeviceOutcome): void => {
    const current = episodes[kind];

    if (isFailureOutcome(outcome)) {
      if (!current) {
        const episode: Episode = { id: newEpisodeId(), reason: outcome, startedAt: now(), attempts: 1 };
        episodes[kind] = episode;
        track(`devices.${kind}.unavailable`, { episodeId: episode.id, reason: outcome, kind });
        return;
      }

      current.attempts += 1;
      // Same failure repeating — no new event, just count the retry.
      if (current.reason === outcome) return;

      // The failure changed shape (e.g. permission_denied → no_device). Keep the
      // same episode so the eventual recovery still joins, but record the change.
      const from = current.reason;
      current.reason = outcome;
      track(`devices.${kind}.unavailable_changed`, {
        episodeId: current.id,
        from,
        reason: outcome,
        attempts: current.attempts,
        kind,
      });
      return;
    }

    // Granted — close the episode if one was open.
    if (current) {
      track(`devices.${kind}.recovered`, {
        episodeId: current.id,
        from: current.reason,
        durationMs: now() - current.startedAt,
        attempts: current.attempts,
        kind,
      });
      delete episodes[kind];
    }
  };

  const flushUnrecovered = (): void => {
    (Object.keys(episodes) as DeviceKind[]).forEach((kind) => {
      const episode = episodes[kind];
      if (!episode) return;
      track(`devices.${kind}.unrecovered`, {
        episodeId: episode.id,
        reason: episode.reason,
        durationMs: now() - episode.startedAt,
        attempts: episode.attempts,
        kind,
      });
      delete episodes[kind];
    });
  };

  return { recordOutcome, flushUnrecovered };
};
